import { format } from "date-fns"
import { BatchTrackingResponse } from "../interface/TrackingInterface"

interface BatchItem {
    cpfCnpj: string
    invoiceNumber: string
    serie: string
    line: number
}

interface BatchResult {
    item: BatchItem
    data?: BatchTrackingResponse
    error?: string
    success: boolean
}

const formatDate = (dateString: string | null): string => {
    if (!dateString) return "N/A"

    try {
        const date = dateString.includes("T")
            ? new Date(dateString)
            : new Date(dateString.replace(" ", "T"))

        return format(date, "dd/MM/yyyy HH:mm:ss")
    } catch {
        return "N/A"
    }
}

const formatCsvField = (field: any): string => {
    if (field === null || field === undefined || field === "") {
        return '""'
    }

    const value = String(field)

    // Sempre força como texto adicionando um espaço no início para números
    // ou usando aspas para strings que podem conter caracteres especiais
    if (/^\d+$/.test(value)) {
        // Para números puros, adiciona espaço para forçar como texto
        return `" ${value}"`
    }

    // Para outros valores, escapa aspas duplas se necessário
    if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`
    }

    return `"${value}"`
}

const generateLastOccurrenceCSV = (results: BatchResult[]) => {
    const headers = [
        "Linha",
        "Numero Nota",
        "Serie",
        "Status",
        "Destinatario",
        "Transportadora",
        "Status Nota",
        "Municipio",
        "UF",
        "Ultima Ocorrencia",
        "Status Ocorrencia",
        "Data Ocorrencia",
        "Erro"
    ]

    const csvData = results.map((result) => {
        const { item, data, error, success } = result

        if (!success) {
            return [
                formatCsvField(item.line),
                formatCsvField(item.invoiceNumber),
                formatCsvField(item.serie),
                "ERRO",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                error || "Erro desconhecido"
            ]
        }

        const lastOccurrence = data!.ocorrencias[data!.ocorrencias.length - 1]

        return [
            formatCsvField(item.line),
            formatCsvField(item.invoiceNumber),
            formatCsvField(item.serie),
            "SUCESSO",
            formatCsvField(data!.notafiscal.destinatario),
            formatCsvField(data!.notafiscal.cnpjTransportadora),
            formatCsvField(data!.notafiscal.Status),
            formatCsvField(data!.notafiscal.endereco.municipio),
            formatCsvField(data!.notafiscal.endereco.uf),
            formatCsvField(lastOccurrence?.descricao || "N/A"),
            formatCsvField(lastOccurrence?.status || "N/A"),
            formatCsvField(formatDate(lastOccurrence?.momento)),
            ""
        ]
    })

    return [headers, ...csvData]
}

const generateAllOccurrencesCSV = (results: BatchResult[]) => {
    const headers = [
        "Linha",
        "Numero Nota",
        "Serie",
        "Status",
        "Destinatario",
        "Transportadora",
        "Status Nota",
        "Municipio",
        "UF",
        "Num Ocorrencia",
        "Descricao Ocorrencia",
        "Status Ocorrencia",
        "Data Ocorrencia",
        "Erro"
    ]

    const csvData: string[][] = []

    results.forEach((result) => {
        const { item, data, error, success } = result

        if (!success) {
            csvData.push([
                formatCsvField(item.line),
                formatCsvField(item.invoiceNumber),
                formatCsvField(item.serie),
                "ERRO",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                error || "Erro desconhecido"
            ])
            return
        }

        data!.ocorrencias.forEach((ocorrencia, index) => {
            csvData.push([
                formatCsvField(item.line),
                formatCsvField(item.invoiceNumber),
                formatCsvField(item.serie),
                "SUCESSO",
                formatCsvField(
                    index === 0 ? data!.notafiscal.destinatario : ""
                ),
                formatCsvField(
                    index === 0 ? data!.notafiscal.cnpjTransportadora : ""
                ),
                formatCsvField(index === 0 ? data!.notafiscal.Status : ""),
                formatCsvField(
                    index === 0 ? data!.notafiscal.endereco.municipio : ""
                ),
                formatCsvField(index === 0 ? data!.notafiscal.endereco.uf : ""),
                formatCsvField(index + 1),
                formatCsvField(ocorrencia.descricao),
                formatCsvField(ocorrencia.status),
                formatCsvField(formatDate(ocorrencia.momento)),
                ""
            ])
        })
    })

    return [headers, ...csvData]
}

export const CsvService = {
    generateCSV: (
        results: BatchResult[],
        type: "last_occurrence" | "all_occurrences"
    ) => {
        const csvData =
            type === "last_occurrence"
                ? generateLastOccurrenceCSV(results)
                : generateAllOccurrencesCSV(results)

        const csvContent = csvData
            .map((row) => row.map((field) => field).join(";"))
            .join("\n")

        const fileType =
            type === "last_occurrence"
                ? "ultima_ocorrencia"
                : "todas_ocorrencias"
        const fileName = `consulta_${fileType}_${format(
            new Date(),
            "yyyyMMdd_HHmmss"
        )}.csv`

        const BOM = "\uFEFF"
        const csvWithBOM = BOM + csvContent

        const blob = new Blob([csvWithBOM], {
            type: "text/csv;charset=utf-8;"
        })
        const link = document.createElement("a")
        const url = URL.createObjectURL(blob)

        link.setAttribute("href", url)
        link.setAttribute("download", fileName)
        link.style.visibility = "hidden"

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }
}
